import { useMedplum } from "@medplum/react-hooks";
import {
  documentFile,
  usePatientDocuments,
  usePatientDocumentsForTraceLookup,
  usePractitioner,
} from "@repo/data-store";
import { DocumentViewer } from "@repo/ui/components/documents/document-viewer";
import { findVogTraceDocument } from "@repo/ui/components/documents/lib/vog-trace";
import { createFileRoute } from "@tanstack/react-router";
import { getPractitionerName } from "@/shared/lib/practitioner-utils";

export const Route = createFileRoute("/patients/$patientId/documents/$documentId")({
  // `?loop=41.2-43.0` shares a video loop. It is the URL's, never the record's.
  validateSearch: (search: Record<string, unknown>): { loop?: string } =>
    typeof search.loop === "string" ? { loop: search.loop } : {},
  component: DocumentDetailComponent,
});

/**
 * A single document — a full-inset route rather than a tab panel, because the
 * browser's PDF viewer wants every pixel it can get. It renders no chrome of
 * its own: the file name and controls come from the viewer, and the way back
 * is the breadcrumb.
 */
function DocumentDetailComponent() {
  const { patientId, documentId } = Route.useParams();
  const { loop } = Route.useSearch();
  const navigate = Route.useNavigate();
  const medplum = useMedplum();
  // Both accessors read the synced collection; only trace lookup includes MAT companions.
  const { documents, isReady } = usePatientDocuments({ patientId });
  const { documents: traceDocuments } = usePatientDocumentsForTraceLookup({ patientId });
  const document = documents.find((candidate) => candidate.id === documentId);

  // Resolved HERE, not in the panel: the panel is presentational, and a
  // component that reads its own data cannot be rendered without standing a
  // data store up first. Only a practitioner is looked up — a patient-uploaded
  // document names the patient, whose name is already on the breadcrumb above.
  const uploader = document?.uploadedBy;
  const { practitioner } = usePractitioner(uploader?.resourceType === "Practitioner" ? uploader.id : undefined);

  if (!document) {
    return <div className="p-6 text-sm text-muted-foreground">{isReady ? "Document not found." : "Loading…"}</div>;
  }

  // The bytes are the server's to store, so there is nothing to render until
  // the upload lands — reachable by deep-linking a row that is still in flight.
  if (!document.binaryId) {
    return <div className="p-6 text-sm text-muted-foreground">Still uploading…</div>;
  }

  const trace = findVogTraceDocument(document, traceDocuments);
  const calibrated = findVogTraceDocument(document, traceDocuments, "calibrated");
  const contentRequest = (id: string) => {
    const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:8104/trpc";
    const url = new URL(`/documents/${encodeURIComponent(id)}/content`, apiUrl);
    return { url: url.href, headers: { Authorization: `Bearer ${medplum.getAccessToken()}` } };
  };
  return (
    <div className="h-full">
      <DocumentViewer
        key={document.binaryId}
        documentKey={document.binaryId}
        {...documentFile(medplum, { binaryId: document.binaryId, title: document.title, size: document.size })}
        fetchRange={() => contentRequest(documentId)}
        traceSource={trace ? { key: trace.id, request: () => contentRequest(trace.id) } : undefined}
        calibratedSource={
          calibrated
            ? { key: calibrated.id, request: () => contentRequest(calibrated.id), fileName: calibrated.sourceFileName }
            : undefined
        }
        loop={loop}
        onLoopChange={(next) => navigate({ search: next ? { loop: next } : {}, replace: true })}
        contentType={document.contentType}
        title={document.title}
        metadata={{
          title: document.title,
          contentType: document.contentType,
          ...(document.size === undefined ? {} : { size: document.size }),
          uploadedAt: document.uploadedAt,
          ...(practitioner ? { uploadedByName: getPractitionerName(practitioner) } : {}),
          ...(document.classification ? { classification: document.classification } : {}),
        }}
      />
    </div>
  );
}
